/* eslint-disable react/jsx-filename-extension */
import React from 'react';
import { Operators } from '@progress/kendo-react-data-tools';
import {
  DateFilter, EnumFilter, CustomersFilter, TextFilter, NumericFilter, BooleanFilter, OrganizationRolesFilter, ChassisPoolFilter,
} from './CustomFilters';
import DispatchersFilter from './CustomFilters/DispatchersFilter';
import LocationsFilter from './CustomFilters/LocationsFilter';
import EquipmentTypesFilter from './CustomFilters/EquipmentTypesFilter';
import ChassisTypesFilter from './CustomFilters/ChassisTypesFilter';
import PortsFilter from './CustomFilters/PortsFilter';
import UsersFilter from './CustomFilters/UsersFilter';
import DocumentTypeFilter from './CustomFilters/DocumentTypeFilter';
import TimeFilter from './CustomFilters/TimeFilter';
import RateZonesFilter from './CustomFilters/RateZonesFilter';
import BrokersFilter from './CustomFilters/BrokersFilter';

const filters = {
  text: {
    operators: () => [
      { text: 'filter.containsOperator', operator: 'contains' },
      { text: 'filter.notContainsOperator', operator: 'doesnotcontain' },
      { text: 'filter.eqOperator', operator: 'eq' },
      { text: 'filter.notEqOperator', operator: 'neq' },
      { text: 'filter.startsWithOperator', operator: 'startswith' },
      { text: 'filter.endsWithOperator', operator: 'endswith' },
      { text: 'filter.isEmptyOperator', operator: 'isempty' },
      { text: 'filter.isNotEmptyOperator', operator: 'isnotempty' },
    ],
    component: ({ fieldsNameForSearch, fieldInfo }) => (props) => (
      <TextFilter {...props} fieldsNameForSearch={fieldsNameForSearch} fieldInfo={fieldInfo} />
    ),
  },
  numeric: {
    operators: ({ nullable }) => [
      { text: 'filter.eqOperator', operator: 'eq' },
      { text: 'filter.notEqOperator', operator: 'neq' },
      { text: 'filter.gteOperator', operator: 'gte' },
      { text: 'filter.gtOperator', operator: 'gt' },
      { text: 'filter.lteOperator', operator: 'lte' },
      { text: 'filter.ltOperator', operator: 'lt' },
      ...(
        nullable
          ? [
            { text: 'filter.isNullOperator', operator: 'isnull' },
            { text: 'filter.isNotNullOperator', operator: 'isnotnull' },
          ]
          : []
      ),
    ],
    component: ({ fieldsNameForSearch, fieldInfo }) => (props) => (
      <NumericFilter {...props} fieldsNameForSearch={fieldsNameForSearch} fieldInfo={fieldInfo} />
    ),
  },
  boolean: {
    operators: ({ nullable }) => [
      ...Operators.boolean,
      ...(
        nullable
          ? [
            { text: 'filter.isNullOperator', operator: 'isnull' },
            { text: 'filter.isNotNullOperator', operator: 'isnotnull' },
          ]
          : []
      ),
    ],
    component: ({ fieldsNameForSearch, fieldInfo }) => (props) => (
      <BooleanFilter {...props} fieldsNameForSearch={fieldsNameForSearch} fieldInfo={fieldInfo} />
    ),
  },
  date: {
    operators: ({ nullable }) => [
      { text: 'filter.eqOperator', operator: 'eq' },
      { text: 'filter.notEqOperator', operator: 'neq' },
      { text: 'filter.afterOrEqualOperator', operator: 'gte' },
      { text: 'filter.afterOperator', operator: 'gt' },
      { text: 'filter.beforeOperator', operator: 'lt' },
      { text: 'filter.beforeOrEqualOperator', operator: 'lte' },
      ...(
        nullable
          ? [
            { text: 'filter.isNullOperator', operator: 'isnull' },
            { text: 'filter.isNotNullOperator', operator: 'isnotnull' },
          ]
          : []
      ),
      { text: 'Between', operator: 'between' },
    ],
    component: ({ fieldsNameForSearch, fieldInfo }) => (props) => (
      <DateFilter {...props} fieldsNameForSearch={fieldsNameForSearch} fieldInfo={fieldInfo} />
    ),
  },
  enum: {
    operators: ({ nullable }) => [
      { text: 'filter.eqOperator', operator: 'eq' },
      { text: 'filter.notEqOperator', operator: 'neq' },
      ...(
        nullable
          ? [
            { text: 'filter.isNullOperator', operator: 'isnull' },
            { text: 'filter.isNotNullOperator', operator: 'isnotnull' },
          ]
          : []
      ),

    ],
    component: ({ lookupValues, fieldsNameForSearch, fieldInfo }) => (props) => (
      <EnumFilter data={lookupValues} fieldsNameForSearch={fieldsNameForSearch} {...props} fieldInfo={fieldInfo} />
    ),
  },
  customers: {
    operators: ({ nullable }) => [
      { text: 'filter.eqOperator', operator: 'eq' },
      { text: 'filter.notEqOperator', operator: 'neq' },
      ...(
        nullable
          ? [
            { text: 'filter.isNullOperator', operator: 'isnull' },
            { text: 'filter.isNotNullOperator', operator: 'isnotnull' },
          ]
          : []
      ),
    ],
    component: ({ fieldsNameForSearch, fieldInfo }) => (props) => (
      <CustomersFilter {...props} fieldsNameForSearch={fieldsNameForSearch} fieldInfo={fieldInfo} />
    ),
  },
  users: {
    operators: ({ nullable }) => [
      { text: 'filter.eqOperator', operator: 'eq' },
      { text: 'filter.notEqOperator', operator: 'neq' },
      ...(
        nullable
          ? [
            { text: 'filter.isNullOperator', operator: 'isnull' },
            { text: 'filter.isNotNullOperator', operator: 'isnotnull' },
          ]
          : []
      ),
    ],
    component: ({ fieldsNameForSearch, fieldInfo }) => (props) => (
      <UsersFilter {...props} fieldsNameForSearch={fieldsNameForSearch} fieldInfo={fieldInfo} />
    ),
  },
  roles: {
    operators: () => [
      { text: 'Contains All', operator: 'contains-all' },
      { text: 'Contains Any', operator: 'contains-any' },
    ],
    component: ({ lookupValues, fieldsNameForSearch, fieldInfo }) => (props) => (
      <OrganizationRolesFilter data={lookupValues} fieldsNameForSearch={fieldsNameForSearch} fieldInfo={fieldInfo} {...props} />
    ),
  },
  chassispools: {
    operators: ({ nullable }) => [
      { text: 'filter.eqOperator', operator: 'eq' },
      { text: 'filter.notEqOperator', operator: 'neq' },
      ...(
        nullable
          ? [
            { text: 'filter.isNullOperator', operator: 'isnull' },
            { text: 'filter.isNotNullOperator', operator: 'isnotnull' },
          ]
          : []
      ),
    ],
    component: ({ fieldsNameForSearch, fieldInfo }) => (props) => (
      <ChassisPoolFilter {...props} fieldsNameForSearch={fieldsNameForSearch} fieldInfo={fieldInfo} />
    ),
  },
  dispatchers: {
    operators: ({ nullable }) => [
      { text: 'filter.eqOperator', operator: 'eq' },
      { text: 'filter.notEqOperator', operator: 'neq' },
      ...(
        nullable
          ? [
            { text: 'filter.isNullOperator', operator: 'isnull' },
            { text: 'filter.isNotNullOperator', operator: 'isnotnull' },
          ]
          : []
      ),
    ],
    component: ({ fieldsNameForSearch, fieldInfo }) => (props) => (
      <DispatchersFilter {...props} fieldsNameForSearch={fieldsNameForSearch} fieldInfo={fieldInfo} />
    ),
  },
  locations: {
    operators: ({ nullable }) => [
      { text: 'filter.eqOperator', operator: 'eq' },
      { text: 'filter.notEqOperator', operator: 'neq' },
      ...(
        nullable
          ? [
            { text: 'filter.isNullOperator', operator: 'isnull' },
            { text: 'filter.isNotNullOperator', operator: 'isnotnull' },
          ]
          : []
      ),
    ],
    component: ({ fieldsNameForSearch, fieldInfo }) => (props) => (
      <LocationsFilter {...props} fieldsNameForSearch={fieldsNameForSearch} fieldInfo={fieldInfo} />
    ),
  },
  equipmenttypes: {
    operators: ({ nullable }) => [
      { text: 'filter.eqOperator', operator: 'eq' },
      { text: 'filter.notEqOperator', operator: 'neq' },
      ...(
        nullable
          ? [
            { text: 'filter.isNullOperator', operator: 'isnull' },
            { text: 'filter.isNotNullOperator', operator: 'isnotnull' },
          ]
          : []
      ),
    ],
    component: ({ fieldsNameForSearch, fieldInfo }) => (props) => (
      <EquipmentTypesFilter {...props} fieldsNameForSearch={fieldsNameForSearch} fieldInfo={fieldInfo} />
    ),
  },
  chassistypes: {
    operators: ({ nullable }) => [
      { text: 'filter.eqOperator', operator: 'eq' },
      { text: 'filter.notEqOperator', operator: 'neq' },
      ...(
        nullable
          ? [
            { text: 'filter.isNullOperator', operator: 'isnull' },
            { text: 'filter.isNotNullOperator', operator: 'isnotnull' },
          ]
          : []
      ),
    ],
    component: ({ fieldsNameForSearch, fieldInfo }) => (props) => (
      <ChassisTypesFilter {...props} fieldsNameForSearch={fieldsNameForSearch} fieldInfo={fieldInfo} />
    ),
  },
  ports: {
    operators: ({ nullable }) => [
      { text: 'filter.eqOperator', operator: 'eq' },
      { text: 'filter.notEqOperator', operator: 'neq' },
      ...(
        nullable
          ? [
            { text: 'filter.isNullOperator', operator: 'isnull' },
            { text: 'filter.isNotNullOperator', operator: 'isnotnull' },
          ]
          : []
      ),
    ],
    component: ({ fieldsNameForSearch, fieldInfo }) => (props) => (
      <PortsFilter {...props} fieldsNameForSearch={fieldsNameForSearch} fieldInfo={fieldInfo} />
    ),
  },
  time: {
    operators: () => [
      ...Operators.date,
    ],
    component: ({ fieldsNameForSearch, fieldInfo }) => (props) => (
      <TimeFilter {...props} fieldsNameForSearch={fieldsNameForSearch} fieldInfo={fieldInfo} />
    ),
  },
  documenttype: {
    operators: ({ nullable }) => [
      { text: 'filter.eqOperator', operator: 'eq' },
      { text: 'filter.notEqOperator', operator: 'neq' },
      ...(
        nullable
          ? [
            { text: 'filter.isNullOperator', operator: 'isnull' },
            { text: 'filter.isNotNullOperator', operator: 'isnotnull' },
          ]
          : []
      ),
    ],
    component: ({ fieldsNameForSearch, fieldInfo }) => (props) => (
      <DocumentTypeFilter {...props} fieldsNameForSearch={fieldsNameForSearch} fieldInfo={fieldInfo} />
    ),
  },
  ratezones: {
    operators: ({ nullable }) => [
      { text: 'filter.eqOperator', operator: 'eq' },
      { text: 'filter.notEqOperator', operator: 'neq' },
      ...(
        nullable
          ? [
            { text: 'filter.isNullOperator', operator: 'isnull' },
            { text: 'filter.isNotNullOperator', operator: 'isnotnull' },
          ]
          : []
      ),
    ],
    component: ({ fieldsNameForSearch, fieldInfo }) => (props) => (
      <RateZonesFilter {...props} fieldsNameForSearch={fieldsNameForSearch} fieldInfo={fieldInfo} />
    ),
  },
  brokers: {
    operators: ({ nullable }) => [
      { text: 'filter.eqOperator', operator: 'eq' },
      { text: 'filter.notEqOperator', operator: 'neq' },
      ...(
        nullable
          ? [
            { text: 'filter.isNullOperator', operator: 'isnull' },
            { text: 'filter.isNotNullOperator', operator: 'isnotnull' },
          ]
          : []
      ),
    ],
    component: ({ fieldsNameForSearch, fieldInfo }) => (props) => (
      <BrokersFilter {...props} fieldsNameForSearch={fieldsNameForSearch} fieldInfo={fieldInfo} />
    ),
  },
};

export default filters;